const DISPLAY_TEXT_KEYS = [
  'text',
  'strength',
  'improvement',
  'feedback',
  'assessment',
  'why',
  'description',
  'recommendation',
] as const;

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const cleanString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const cleaned = value.trim();
  return cleaned.length > 0 ? cleaned : null;
};

/**
 * Converts evaluator feedback into display-safe text.
 *
 * AI providers occasionally return a structured feedback object inside fields
 * whose preferred schema is string[]. Rendering that object directly crashes
 * React. Keep the UI resilient while preserving the most useful explanation.
 */
export const formatEvaluationFeedbackItem = (value: unknown): string | null => {
  const directText = cleanString(value);
  if (directText) return directText;

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (!isRecord(value)) return null;

  const primaryText = DISPLAY_TEXT_KEYS
    .map((key) => cleanString(value[key]))
    .find((entry): entry is string => Boolean(entry));
  const quote = cleanString(value.quote);
  const suggestedAlternative = cleanString(value.suggested_alternative);

  if (primaryText && quote && primaryText !== quote) {
    return `${primaryText} (Example: “${quote}”)`;
  }
  if (primaryText) return primaryText;
  if (suggestedAlternative) return `Suggested alternative: ${suggestedAlternative}`;
  if (quote) return quote;

  return null;
};

/** Normalises an unknown evaluator list into strings that React and exports can safely render. */
export const normaliseEvaluationFeedbackList = (value: unknown): string[] => {
  const entries = Array.isArray(value) ? value : value == null ? [] : [value];

  return entries
    .map(formatEvaluationFeedbackItem)
    .filter((entry): entry is string => Boolean(entry));
};
